import * as React from 'react';
import { Input as AntInput, Select, message } from 'antd';

import { Button, buttonType } from 'components/button/button';
import { Modal } from 'components/modal/modal';
import {
    MarketingActivity,
    MarketingActivityStatus,
    MarketingActivityType,
    activityStatusBadges,
    activityTypeTitles,
} from 'core/domain';
import { createMarketingActivity, updateMarketingActivity } from 'core/repositories/marketing';

const { useEffect, useState } = React;

const statusOptions = Object.entries(activityStatusBadges).map(([value, badge]) => ({
    value: Number(value),
    label: badge.text,
}));

const typeOptions = Object.entries(activityTypeTitles).map(([value, label]) => ({ value: Number(value), label }));

interface CreateProps {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    userId: string;
    onSaved: () => void;
}

/** ثبت دستی اکتیویتی برای یک کاربر. */
export const CreateActivityModal: React.FC<CreateProps> = ({ isOpen, setIsOpen, userId, onSaved }) => {
    const [type, setType] = useState(MarketingActivityType.Call);
    const [status, setStatus] = useState(MarketingActivityStatus.Done);
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setType(MarketingActivityType.Call);
        setStatus(MarketingActivityStatus.Done);
        setDescription('');
    }, [isOpen]);

    const save = async () => {
        setSaving(true);
        const result = await createMarketingActivity({ userId, type, status, description: description.trim() });
        setSaving(false);
        if (!result) return;
        message.success('اکتیویتی ثبت شد.');
        setIsOpen(false);
        onSaved();
    };

    return (
        <Modal isOpen={isOpen} setIsOpen={setIsOpen} className="mk-modal">
            <h4>ثبت اکتیویتی</h4>
            <div className="mk-form">
                <div className="mk-row">
                    <label className="mk-field">
                        نوع
                        <Select value={type} options={typeOptions} onChange={setType} />
                    </label>
                    <label className="mk-field">
                        وضعیت
                        <Select value={status} options={statusOptions} onChange={setStatus} />
                    </label>
                </div>
                <label className="mk-field">
                    توضیحات
                    <AntInput.TextArea
                        rows={4}
                        maxLength={2000}
                        value={description}
                        placeholder="نتیجه‌ی تماس، خلاصه‌ی گفتگو و ..."
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </label>
            </div>
            <div className="buttons">
                <Button type={buttonType.outlined} text="انصراف" onClick={() => setIsOpen(false)} />
                <Button type={buttonType.info} text="ثبت" loading={saving} onClick={save} />
            </div>
        </Modal>
    );
};

interface UpdateProps {
    activity?: Pick<MarketingActivity, 'id' | 'type' | 'status' | 'description'>;
    setActivity: (activity?: UpdateProps['activity']) => void;
    onSaved: () => void;
    title?: string;
}

/** تغییر وضعیت / ثبت نتیجه‌ی اکتیویتی (مثلا done کردن تسک تماس). */
export const UpdateActivityModal: React.FC<UpdateProps> = ({ activity, setActivity, onSaved, title }) => {
    const [status, setStatus] = useState(MarketingActivityStatus.Done);
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setIsOpen(!!activity);
        if (!activity) return;
        // تسکی که در حال انجام است معمولا برای done شدن باز می‌شود.
        setStatus(
            activity.status === MarketingActivityStatus.Doing || activity.status === MarketingActivityStatus.Todo
                ? MarketingActivityStatus.Done
                : activity.status,
        );
        setDescription(activity.description ?? '');
    }, [activity]);

    const close = () => setActivity(undefined);

    const save = async () => {
        if (!activity) return;
        setSaving(true);
        const ok = await updateMarketingActivity({ id: activity.id, status, description: description.trim() });
        setSaving(false);
        if (!ok) return;
        message.success('وضعیت اکتیویتی ذخیره شد.');
        close();
        onSaved();
    };

    return (
        <Modal isOpen={isOpen} setIsOpen={setIsOpen} onClose={close} className="mk-modal">
            <h4>
                {title ?? 'ثبت نتیجه'}
                {activity ? ` — ${activityTypeTitles[activity.type]}` : ''}
            </h4>
            <div className="mk-form">
                <label className="mk-field">
                    وضعیت
                    <Select value={status} options={statusOptions} onChange={setStatus} />
                </label>
                <label className="mk-field">
                    توضیحات
                    <AntInput.TextArea
                        rows={4}
                        maxLength={2000}
                        value={description}
                        placeholder="نتیجه‌ی تماس، خلاصه‌ی گفتگو و ..."
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </label>
            </div>
            <div className="buttons">
                <Button type={buttonType.outlined} text="بعدا" onClick={close} />
                <Button type={buttonType.info} text="ذخیره" loading={saving} onClick={save} />
            </div>
        </Modal>
    );
};
